import React, { useState } from 'react';
import { Check, X, Edit, Trash2, Plus } from 'lucide-react';

const TodoList = ({ initialTodos = [] }) => {
  const [todos, setTodos] = useState(initialTodos);
  const [newTask, setNewTask] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newTime, setNewTime] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editTask, setEditTask] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editTime, setEditTime] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  const addTodo = () => {
    if (newTask.trim()) {
      const newTodo = {
        id: Date.now().toString(),
        task: newTask,
        category: newCategory,
        time: newTime,
        completed: false,
      };
      setTodos([...todos, newTodo]);
      setNewTask('');
      setNewCategory('');
      setNewTime('');
      setShowAddForm(false);
    }
  };

  const toggleComplete = (id) => {
    setTodos(todos.map(todo =>
      todo.id === id ? { ...todo, completed: !todo.completed } : todo
    ));
  };

  const deleteTodo = (id) => {
    setTodos(todos.filter(todo => todo.id !== id));
  };

  const startEdit = (todo) => {
    setEditingId(todo.id);
    setEditTask(todo.task);
    setEditCategory(todo.category);
    setEditTime(todo.time);
  };

  const saveEdit = () => {
    if (editingId && editTask.trim()) {
      setTodos(todos.map(todo =>
        todo.id === editingId
          ? { ...todo, task: editTask, category: editCategory, time: editTime }
          : todo
      ));
      setEditingId(null);
      setEditTask('');
      setEditCategory('');
      setEditTime('');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTask('');
    setEditCategory('');
    setEditTime('');
  };

  return (
    <div className="bg-white rounded-xl p-6 border-2 border-solid border-black">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">To Do List</h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center space-x-1 px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
        >
          <Plus size={16} />
          <span>Add</span>
        </button>
      </div>

      {/* Add new todo form */}
      {showAddForm && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
          <input
            type="text"
            placeholder="Task name"
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            className="w-full mb-2 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder="Category (optional)"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="w-full mb-2 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="time"
            value={newTime}
            onChange={(e) => setNewTime(e.target.value)}
            className="w-full mb-2 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex space-x-2">
            <button
              onClick={addTodo}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
            >
              Add Task
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewTask('');
                setNewCategory('');
                setNewTime('');
              }}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Todo list */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {todos.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No tasks yet. Add your first task!</p>
        ) : (
          todos.map((todo) => (
            <div key={todo.id} className="border-b border-gray-200 pb-4">
              {editingId === todo.id ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editTask}
                    onChange={(e) => setEditTask(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    placeholder="Category"
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="time"
                    value={editTime}
                    onChange={(e) => setEditTime(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={saveEdit}
                      className="px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors text-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <button
                      onClick={() => toggleComplete(todo.id)}
                      className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-1 transition-colors ${
                        todo.completed
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'border-gray-300 hover:border-green-500'
                      }`}
                    >
                      {todo.completed && <Check size={12} />}
                    </button>
                    <div className="flex-1">
                      <h3 className={`font-medium ${todo.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                        {todo.task}
                      </h3>
                      {todo.category && (
                        <p className="text-sm text-gray-600 mt-1">{todo.category}</p>
                      )}
                      {todo.time && (
                        <p className="text-sm text-gray-500 mt-1">{todo.time}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => startEdit(todo)}
                      className="p-1 text-blue-500 hover:text-blue-700 transition-colors"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => deleteTodo(todo.id)}
                      className="p-1 text-red-500 hover:text-red-700 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TodoList;